/**
 * IDE migration configurations
 *
 * IMPORTANT: Keep in sync with:
 *   - docs/references/IDE-STANDARDS.md (Appendix F: IDE Configuration Registry)
 *   - src/config/ide-formats.ts (Detection configs)
 *
 * Jai1 Source Format (Windsurf/Antigravity compatible):
 * - trigger: always_on | always | manual | glob_pattern
 *
 * Transformation rules:
 * - Cursor: trigger=always_on/always → alwaysApply=true, trigger=glob → globs=pattern
 * - Windsurf: trigger=always_on → trigger=always_on, trigger=glob → trigger=pattern
 * - Antigravity: trigger=always_on → trigger=always_on (same as source)
 * - Claude Code: trigger=always_on → no paths field, trigger=glob → paths=pattern
 * - OpenCode: trigger=always_on → trigger=always_on, trigger=glob → trigger=pattern
 * - Codex: rules merge into AGENTS.md; workflows become skills in .agents/skills
 */
import type { IDEMigrationConfig, FrontmatterOptions, MigrateIDE } from '../types/migrate-ide.types.js';

/**
 * Check if trigger is an "always" type trigger
 */
function isAlwaysTrigger(trigger?: string): boolean {
    return trigger === 'always_on' || trigger === 'always';
}

/**
 * Get glob pattern from trigger (if not an always/manual trigger)
 */
function getGlobFromTrigger(trigger?: string, globs?: string[]): string[] | undefined {
    // If explicit globs are provided, use them
    if (globs && globs.length > 0) return globs;

    // If trigger is a glob pattern (not always/always_on/manual), use it
    if (trigger && !isAlwaysTrigger(trigger) && trigger !== 'manual') {
        return [trigger];
    }

    return undefined;
}

export const IDE_MIGRATION_CONFIGS: Record<MigrateIDE, IDEMigrationConfig> = {
    cursor: {
        id: 'cursor',
        name: 'Cursor',
        icon: '🔮',
        basePath: '.cursor',
        rulesPath: 'rules',
        workflowsPath: null,
        commandsPath: 'commands',
        skillsPath: '../.agents/skills',
        fileExtension: '.mdc',         // For rules
        workflowsExtension: '.md',     // For workflows/commands
        generateFrontmatter: (opts: FrontmatterOptions) => {
            const lines = ['---'];

            if (opts.description) {
                lines.push(`description: ${opts.description}`);
            }

            // Determine alwaysApply from trigger or explicit flag
            const alwaysApply = opts.alwaysApply || isAlwaysTrigger(opts.trigger);

            // Get globs from explicit globs or trigger pattern
            const globs = getGlobFromTrigger(opts.trigger, opts.globs);
            if (globs && globs.length > 0) {
                lines.push(`globs: ${globs.join(', ')}`);
            }

            lines.push(`alwaysApply: ${alwaysApply}`);
            lines.push('---');
            return lines.join('\n');
        },
    },
    windsurf: {
        id: 'windsurf',
        name: 'Windsurf',
        icon: '🏄',
        basePath: '.windsurf',
        rulesPath: 'rules',
        workflowsPath: 'workflows',
        commandsPath: null,
        skillsPath: 'skills',
        fileExtension: '.md',
        flattenSubdirs: true,
        generateFrontmatter: (opts: FrontmatterOptions) => {
            // Windsurf uses trigger field, same as Jai1 source format
            // But Windsurf prefers 'always_on' for always-active rules
            let trigger: string;

            if (opts.trigger) {
                // Use source trigger directly, normalize 'always' to 'always_on'
                trigger = opts.trigger === 'always' ? 'always_on' : opts.trigger;
            } else if (opts.alwaysApply) {
                trigger = 'always_on';
            } else if (opts.globs && opts.globs.length > 0) {
                trigger = opts.globs[0]!;
            } else {
                trigger = 'always_on';
            }

            return `---\ntrigger: ${trigger}\n---`;
        },
    },
    antigravity: {
        id: 'antigravity',
        name: 'Antigravity',
        icon: '🚀',
        basePath: '.agent',
        rulesPath: 'rules',
        workflowsPath: 'workflows',
        commandsPath: null,
        skillsPath: 'skills',
        fileExtension: '.md',
        flattenSubdirs: true,
        generateFrontmatter: (opts: FrontmatterOptions) => {
            // Antigravity uses trigger field, same as Jai1 source format
            // Antigravity prefers 'always_on' for always-active rules
            let trigger: string;

            if (opts.trigger) {
                // Use source trigger directly, normalize 'always' to 'always_on'
                trigger = opts.trigger === 'always' ? 'always_on' : opts.trigger;
            } else if (opts.alwaysApply) {
                trigger = 'always_on';
            } else if (opts.globs && opts.globs.length > 0) {
                trigger = opts.globs[0]!;
            } else {
                trigger = 'always_on';
            }

            return `---\ntrigger: ${trigger}\n---`;
        },
    },
    claude: {
        id: 'claude',
        name: 'Claude Code',
        icon: '🤖',
        basePath: '.claude',
        rulesPath: 'rules',
        workflowsPath: null,
        commandsPath: 'commands',
        skillsPath: 'skills',
        fileExtension: '.md',
        generateFrontmatter: (opts: FrontmatterOptions) => {
            const lines = ['---'];

            if (opts.description) {
                lines.push(`description: ${opts.description}`);
            }

            // Claude Code uses 'paths' instead of 'globs'
            // If no paths, rule is always applied
            const isAlways = opts.alwaysApply || isAlwaysTrigger(opts.trigger);

            if (!isAlways) {
                const paths = getGlobFromTrigger(opts.trigger, opts.globs);
                if (paths && paths.length > 0) {
                    lines.push(`paths: ${paths.join(', ')}`);
                }
            }

            lines.push('---');
            return lines.join('\n');
        },
    },
    opencode: {
        id: 'opencode',
        name: 'OpenCode',
        icon: '💻',
        basePath: '.opencode',
        rulesPath: null, // OpenCode uses AGENTS.md for rules
        workflowsPath: null,
        commandsPath: 'command', // Note: singular "command" not "commands"
        skillsPath: '../.agents/skills',
        fileExtension: '.md',
        generateFrontmatter: (opts: FrontmatterOptions) => {
            // OpenCode command frontmatter includes: description, agent, model
            const lines = ['---'];

            if (opts.description) {
                lines.push(`description: ${opts.description}`);
            }

            // Add agent field (default to 'build' if not specified)
            const agent = opts.agent ?? 'build';
            lines.push(`agent: ${agent}`);

            // Add model field (optional)
            if (opts.model) {
                lines.push(`model: ${opts.model}`);
            }

            lines.push('---');
            return lines.join('\n');
        },
    },
    codex: {
        id: 'codex',
        name: 'Codex',
        icon: '🧠',
        basePath: '.',
        rulesPath: null, // Codex uses AGENTS.md for prompt instructions
        workflowsPath: null, // Workflows are converted to skills by MigrateIdeService
        commandsPath: null,
        skillsPath: '.agents/skills',
        fileExtension: '.md',
        generateFrontmatter: () => '',
    },
};

export function getMigrationIDEs(): MigrateIDE[] {
    return Object.keys(IDE_MIGRATION_CONFIGS) as MigrateIDE[];
}

export function getIDEConfig(ide: MigrateIDE): IDEMigrationConfig | undefined {
    return IDE_MIGRATION_CONFIGS[ide];
}
